// Observed schedule-reliability derived from the real ETD-drift volatility block (departure
// punctuality over the trailing window — NOT arrival on-time, which stays lane-scoped in /compare).
// Kept byte-identical in wording to reliabilityFacts()/reliabilitySentence() in prerender.carriers.mjs
// so the static HTML, the hydrated app, and the JSON-LD all read the same. Data-gated: thin/absent
// samples return null so pages degrade gracefully instead of stating a figure we can't stand behind.

export interface CarrierVolatilityLike {
  active_sailings: number
  revisions_per_sailing: number
  p90_abs_etd_hours: number
  late_etd_share: number
  window_days: number
}
export interface CarrierStatsLike {
  sailings: number
  lanes: number
  volatility: CarrierVolatilityLike | null
}
export interface ReliabilityFacts {
  n: number
  windowDays: number
  behindPct: number
  onSchedulePct: number
  p90Hours: number
  p90Days: number
  revisionsPerSailing: number
}

// Below this many observed sailings a figure is labelled low-sample rather than stated — mirrors the
// API reliability sample gate (MEMORY: reliability-sample-gate). Never lower it.
export const RELIABILITY_MIN_OBSERVATIONS = 30

export function reliabilityFacts(
  stats: CarrierStatsLike | undefined | null
): ReliabilityFacts | null {
  const v = stats?.volatility
  if (!v || v.active_sailings == null || v.active_sailings < RELIABILITY_MIN_OBSERVATIONS)
    return null
  return {
    n: v.active_sailings,
    windowDays: v.window_days,
    behindPct: Math.round(v.late_etd_share * 1000) / 10,
    onSchedulePct: Math.round((1 - v.late_etd_share) * 1000) / 10,
    p90Hours: v.p90_abs_etd_hours,
    p90Days: Math.round((v.p90_abs_etd_hours / 24) * 10) / 10,
    revisionsPerSailing: v.revisions_per_sailing,
  }
}

export function reliabilitySentence(carrierName: string, f: ReliabilityFacts): string {
  return `Over the last ${f.windowDays} days, ${carrierName} departed on schedule about ${f.onSchedulePct}% of the time across ${f.n.toLocaleString('en-US')} observed sailings, with roughly ${f.behindPct}% of departures behind schedule and 90th-percentile schedule drift of about ${f.p90Hours} hours (${f.p90Days} days).`
}
